/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import CannedCollectionModule from '@coursera-web/cds-labs/components/Modules/CannedCollectionModule/CannedCollectionModule';

import { isRightToLeft } from 'js/lib/language';

import { breakpoints, typography2 } from '@coursera/cds-core';
import type { MerchandisingModuleModuleType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import Section from 'bundles/front-page/components/Section';
import getPromoUnitProps from 'bundles/front-page/redesign-modules/promo-units/getPromoUnitProps';

import _t from 'i18n!nls/front-page';

const styles = {
  promoUnitsModule: (isRtl: boolean) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;

    /* Outermost container for CannedCollectionModule */
    > div {
      display: flex !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* Grid container for CannedCollectionModule */
    > div > div {
      padding: 0 !important;
      margin: 0;
    }

    /* Grid item wrapper for CannedCollectionModule */
    > div > div > div {
      margin: 0 !important;
      padding: 0 !important;
      gap: 0;
    }

    /* Card container */
    li {
      /* Card content */
      > a > div > div {
        min-height: auto;
        padding: ${isRtl
          ? 'var(--cds-spacing-300) var(--cds-spacing-300) var(--cds-spacing-300) 0'
          : 'var(--cds-spacing-300) 0 var(--cds-spacing-300) var(--cds-spacing-300)'};

        ${breakpoints.down('sm')} {
          /* Adjust padding to allow background image to extend full width */
          padding: var(--cds-spacing-300) var(--cds-spacing-300) 0 var(--cds-spacing-300) !important;

          /* Background image container */
          > div:last-of-type {
            margin-left: ${isRtl ? '0' : 'calc(var(--cds-spacing-300) * -1)'};
            margin-right: ${isRtl ? 'calc(var(--cds-spacing-300) * -1)' : '0'};
            margin-top: var(--cds-spacing-300);
            width: calc(100% + var(--cds-spacing-600));

            img {
              width: 100%;
              vertical-align: baseline;
              max-height: 100% !important;
              max-width: 100% !important;
            }
          }
        }
      }

      /* Card eyebrow */
      img:first-of-type {
        vertical-align: bottom;

        ${breakpoints.down('sm')} {
          max-height: 14px !important;
          max-width: 70% !important;
        }
      }

      /* Card headline */
      h3 {
        ${typography2.subtitleLarge};
        margin-bottom: 0;

        ${breakpoints.down('sm')} {
          margin-bottom: var(--cds-spacing-100);
        }
      }

      /* Card CTA */
      button.cds-button-ghostInvert {
        margin-top: var(--cds-spacing-100);
        padding: 0;

        .cds-button-focusVisible {
          outline-color: var(--cds-color-interactive-stroke-primary-focus);
        }

        /* Style ghost CTAs with secondary variant styling for tablet and smaller */
        ${breakpoints.down('sm')} {
          margin-top: var(--cds-spacing-50);
          padding: var(--cds-spacing-100) var(--cds-spacing-200);
          background-color: var(--cds-color-neutral-background-primary-weak);
          color: var(--cds-color-interactive-primary);
          box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary);

          &:hover {
            color: var(--cds-color-interactive-primary-hover);
            box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary-hover);
            background-color: var(--cds-color-interactive-background-primary-hover-weak);
          }

          .cds-button-focusVisible {
            outline: transparent solid 1px;
            outline-offset: 2px;
            box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary-hover);
            background-color: var(--cds-color-interactive-background-primary-hover-weak);
            color: var(--cds-color-interactive-primary-hover);
          }

          .cds-button-startIcon,
          .cds-button-endIcon {
            display: none;
          }

          span.cds-button-label {
            ${typography2.actionSecondary};
          }
        }
      }
    }
  `,
};

const PromoUnitsModuleRenderer = () => {
  const isRtl: boolean = isRightToLeft(_t.getLocale());
  const track = useTracker();

  const { courseraPlusProps, courseraForBusinessProps } = getPromoUnitProps();

  const handleCourseraPlusClick = () => {
    track('click_merchandising_module', {
      merchandisingModule: {
        moduleType: 'promo_unit' as MerchandisingModuleModuleType,
        heading: courseraPlusProps.headline,
      },
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkName: courseraPlusProps.ctaText,
        linkURL: courseraPlusProps.target,
      },
    });
  };

  const handleCourseraForBusinessClick = () => {
    track('click_merchandising_module', {
      merchandisingModule: {
        moduleType: 'promo_unit' as MerchandisingModuleModuleType,
        heading: courseraForBusinessProps.headline,
      },
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkName: courseraForBusinessProps.ctaText,
        linkURL: courseraForBusinessProps.target,
      },
    });
  };

  const courseraPlusCard = { ...courseraPlusProps, onClick: handleCourseraPlusClick };
  const courseraForBusinessCard = { ...courseraForBusinessProps, onClick: handleCourseraForBusinessClick };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'canned_collection',
      },
    },
    [],
    { fullyVisible: true }
  );

  return (
    <Section css={styles.promoUnitsModule(isRtl)}>
      <div ref={visibilityTrackingRef}>
        <CannedCollectionModule
          cards={[courseraPlusCard, courseraForBusinessCard]}
          size="large"
          color="blue"
          showImageInFull={true}
          showAllEntries={true}
          variantConfig={{
            sm: 'grid',
          }}
        />
      </div>
    </Section>
  );
};

export default PromoUnitsModuleRenderer;
